import React, { useCallback, useReducer } from 'react';
import { IReportsWithCustomProps } from '../../../../reportsContainerState/reportsContainerContext';
import { reportsReducer } from './reportsReducer';
import { TTypeOfModalWindow, ReportsContext, reportsInitialState, IEmails, IDataForSubmit } from './reportsContext';
import { TTypeOfSchedule, TTypeOfReport } from 'types';
import { availableScheduleTypesForFeesReport } from 'appConstants';
import { ReportsActionsTypes } from '../reportsAction';

const ReportsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(reportsReducer, reportsInitialState);

	const setHasBanOnChanges = useCallback(() => {
		dispatch({
			type: ReportsActionsTypes.SET_HAS_BAN_ON_CHANGES,
			payload: {
				hasBanOnChanges: true,
			},
		});
	}, []);

	const setAllData = useCallback((value: IReportsWithCustomProps) => {
		dispatch({
			type: ReportsActionsTypes.SET_ALL_DATA,
			payload: value,
		});
	}, []);

	const setIDReportType = useCallback(
		(value: null | TTypeOfReport) => {
			const isFeesReport = value === 'Fees';

			dispatch({
				type: ReportsActionsTypes.SET_ID_REPORT_TYPE,
				payload: {
					IDReportType: value,
					sendingReportAfterShiftClosing: isFeesReport ? false : state.sendingReportAfterShiftClosing,
					IDsScheduleTypes: isFeesReport
						? state.IDsScheduleTypes.filter((el) => availableScheduleTypesForFeesReport.includes(el))
						: state.IDsScheduleTypes,
				},
			});
		},
		[state.sendingReportAfterShiftClosing, state.IDsScheduleTypes],
	);

	const setIDsScheduleTypes = useCallback((value: TTypeOfSchedule[]) => {
		dispatch({
			type: ReportsActionsTypes.SET_IDS_SCHEDULE_TYPES,
			payload: {
				IDsScheduleTypes: value,
			},
		});
	}, []);

	const addEmails = useCallback(
		(value: IEmails) => {
			dispatch({
				type: ReportsActionsTypes.CHANGE_EMAILS,
				payload: {
					emails: [...state.emails, value],
				},
			});
		},
		[state.emails],
	);

	const changeEmails = useCallback(
		(emailId: string, email: string) => {
			const updatedEmails = state.emails.map((el) => (el.id === emailId ? { id: el.id, email } : el));
			dispatch({
				type: ReportsActionsTypes.CHANGE_EMAILS,
				payload: {
					emails: updatedEmails,
				},
			});
		},
		[state.emails],
	);

	const deleteEmails = useCallback(
		(emailId: string) => {
			const updatedEmails = state.emails.filter((el) => el.id !== emailId);
			dispatch({
				type: ReportsActionsTypes.CHANGE_EMAILS,
				payload: {
					emails: updatedEmails,
				},
			});
		},
		[state.emails],
	);

	const addCopyEmails = useCallback(
		(value: IEmails) => {
			dispatch({
				type: ReportsActionsTypes.CHANGE_COPY_EMAILS,
				payload: {
					copyEmails: [...state.copyEmails, value],
				},
			});
		},
		[state.copyEmails],
	);

	const changeCopyEmails = useCallback(
		(copyEmailId: string, email: string) => {
			const updatedCopyEmails = state.copyEmails.map((el) => (el.id === copyEmailId ? { id: el.id, email } : el));
			dispatch({
				type: ReportsActionsTypes.CHANGE_COPY_EMAILS,
				payload: {
					copyEmails: updatedCopyEmails,
				},
			});
		},
		[state.copyEmails],
	);

	const deleteCopyEmails = useCallback(
		(copyEmailId: string) => {
			const updatedEmails = state.copyEmails.filter((el) => el.id !== copyEmailId);
			dispatch({
				type: ReportsActionsTypes.CHANGE_COPY_EMAILS,
				payload: {
					copyEmails: updatedEmails,
				},
			});
		},
		[state.copyEmails],
	);

	const toggleSendingReportAfterShiftClosing = useCallback(() => {
		dispatch({ type: ReportsActionsTypes.TOGGLE_SENDING_REPORT_AFTER_SHIFT_CLOSING });
	}, []);

	const setDataForSubmit = useCallback((value: IDataForSubmit | null) => {
		dispatch({
			type: ReportsActionsTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ReportsActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<ReportsContext.Provider
			value={{
				setHasBanOnChanges,
				setAllData,
				setIDReportType,
				setIDsScheduleTypes,
				addEmails,
				changeEmails,
				deleteEmails,
				addCopyEmails,
				changeCopyEmails,
				deleteCopyEmails,
				toggleSendingReportAfterShiftClosing,
				setDataForSubmit,
				changeTypeOfModalWindow,
				hasBanOnChanges: state.hasBanOnChanges,
				restaurantId: state.restaurantId,
				id: state.id,
				IDReportType: state.IDReportType,
				IDsScheduleTypes: state.IDsScheduleTypes,
				emails: state.emails,
				copyEmails: state.copyEmails,
				sendingReportAfterShiftClosing: state.sendingReportAfterShiftClosing,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ReportsContext.Provider>
	);
};

export default ReportsState;
